import { useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import store, { dispatch } from 'store'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataMediaThunks } from 'store/features/projectData/thunks/media'
import { checkStorageBeforeUpload } from 'store/features/user/utils'
import { initialDropState, useTimelineContext } from 'context/TimelineContext'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { DEFAULT_MEDIA_DURATION } from 'consts'
import { IMediaItem } from 'types/media'
import { secondsToPixels, computePreviewMediaItems } from 'utils/timeline'
import {
  calculateTotalFilesSize,
  calculateTotalMediaDuration
} from 'utils/media'
import { useFileFormatValidator } from 'hooks/useFileFormatValidator'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { useTimelineDropPosition } from 'hooks/useTimelineDropPosition'
import {
  assignStartTimes,
  createMediaItems
} from 'hooks/project-item-upload/use-project-media-upload/helpers'
import { getDropLineKey } from 'utils/timelineZIndex'
import { isValueUndefined } from 'utils'

//this hook is used for OS files dnd
export const useTimelineDropZone = () => {
  const { setPreviewMediaItems, setDropState } = useTimelineContext()

  // Real file durations can't be read before drop, so we use a placeholder
  // duration (count × DEFAULT_MEDIA_DURATION) for the ghost preview.
  const dragDurationRef = useRef(0)
  // Keyed on insertTime + targetZIndex + targetGapAfterLineIndex so vertical
  // row changes and gap↔line transitions still trigger an update at the
  // same X.
  const lastDropKeyRef = useRef<string | null>(null)

  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const existingMediaItems = useSelector(projectDataSelectors.mediaItems)
  const defaultDimensions = useSelector(projectDataSelectors.defaultDimensions)
  const { isDurationAllowed } = useDurationLimitCheck()
  const validateFiles = useFileFormatValidator('visualMedia')
  const { setAlertPopupsData } = useAlertPopupsContext()
  const { getDropPositionFromClient } = useTimelineDropPosition()

  const clearDragState = useCallback(() => {
    setDropState(initialDropState)
    setPreviewMediaItems(null)
    dragDurationRef.current = 0
    lastDropKeyRef.current = null
  }, [setDropState, setPreviewMediaItems])

  const addFilesAtTime = useCallback(
    async (
      files: FileList | File[],
      startTime: number,
      previousItemId?: string,
      targetZIndex?: number,
      targetGapAfterLineIndex?: number
    ) => {
      const validFiles = validateFiles(Array.from(files))

      if (validFiles.length === 0) return

      const totalFilesSize = calculateTotalFilesSize(validFiles)

      const { canUpload } = await checkStorageBeforeUpload(
        totalFilesSize,
        dispatch,
        store.getState
      )

      if (!canUpload) {
        setAlertPopupsData({ fullStoragePopup: { isOpen: true } })
        return
      }

      const newMediaItems = await createMediaItems(
        validFiles,
        defaultDimensions
      )
      const totalNewDuration = calculateTotalMediaDuration(newMediaItems)

      if (!isDurationAllowed(totalNewDuration)) return

      const positionedItems = assignStartTimes(newMediaItems, startTime).map(
        item =>
          targetZIndex !== undefined ? { ...item, zIndex: targetZIndex } : item
      )
      dispatch(
        projectDataMediaThunks.adjustTimelineForNewItems(
          positionedItems,
          previousItemId,
          targetZIndex,
          targetGapAfterLineIndex
        )
      )

      const uploadItems = validFiles.map((file, index) => ({
        id: positionedItems[index].id,
        type: positionedItems[index].type,
        file
      }))

      dispatch(projectDataThunks.uploadMediasToProject(uploadItems))
    },
    [validateFiles, isDurationAllowed, setAlertPopupsData, defaultDimensions]
  )

  const onDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return
      event.preventDefault()
      event.stopPropagation()

      setDropState(prev => ({ ...prev, isDragOver: true }))
      const itemCount = event.dataTransfer.items.length || 1
      dragDurationRef.current = itemCount * DEFAULT_MEDIA_DURATION
    },
    [setDropState]
  )

  const onDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return
      event.preventDefault()
      event.stopPropagation()
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        clearDragState()
      }
    },
    [clearDragState]
  )

  const onDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return
      event.preventDefault()
      event.stopPropagation()
      event.dataTransfer.dropEffect = 'copy'

      const dragDuration = dragDurationRef.current
      if (dragDuration <= 0) return

      const { insertTime, targetZIndex, targetGapAfterLineIndex } =
        getDropPositionFromClient(event.clientX, event.clientY)
      if (insertTime === undefined || targetZIndex === undefined) return

      const isGapDrop = !isValueUndefined(targetGapAfterLineIndex)

      const dropKey = getDropLineKey({
        isGapDrop,
        insertTime,
        targetGapAfterLineIndex,
        targetZIndex
      })
      if (lastDropKeyRef.current === dropKey) return
      lastDropKeyRef.current = dropKey

      setDropState({
        isDragOver: true,
        rowZIndex: targetZIndex,
        // For gap drops the precise X / row position would only mislead — the
        // BetweenLinesGap highlight is the visual feedback. Suppress the
        // DropIndicator by leaving position/width null.
        positionPx: isGapDrop
          ? null
          : secondsToPixels(insertTime, timelineRulerSliceDuration),
        widthPx: isGapDrop
          ? null
          : secondsToPixels(dragDuration, timelineRulerSliceDuration),
        targetGapAfterLineIndex: targetGapAfterLineIndex ?? null
      })

      // No cascade preview for gap drops — the new row is empty.
      if (isGapDrop) {
        setPreviewMediaItems(null)
      } else {
        const preview: IMediaItem[] = computePreviewMediaItems(
          existingMediaItems,
          insertTime,
          dragDuration,
          targetZIndex
        )
        setPreviewMediaItems(preview)
      }
    },
    [
      existingMediaItems,
      timelineRulerSliceDuration,
      setDropState,
      setPreviewMediaItems,
      getDropPositionFromClient
    ]
  )

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return
      event.preventDefault()
      event.stopPropagation()

      const files = event.dataTransfer.files
      clearDragState()

      if (!files || files.length === 0) return

      const {
        insertTime,
        previousItemId,
        targetZIndex,
        targetGapAfterLineIndex
      } = getDropPositionFromClient(event.clientX, event.clientY)
      if (insertTime === undefined) return

      addFilesAtTime(
        files,
        insertTime,
        previousItemId,
        targetZIndex,
        targetGapAfterLineIndex
      )
    },
    [clearDragState, addFilesAtTime, getDropPositionFromClient]
  )

  return { onDragEnter, onDragLeave, onDragOver, onDrop }
}
